import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import usePortalAuth from '../../../auth/usePortalAuth';
import basePCUrl from '../../../Globals';
import { postApiData } from '../../../utils/service';

const PUBLIC_BUCKET_BASE =
  process.env.REACT_APP_S3_PUBLIC_BASE_URL ||
  'https://portal-upload-repository.s3.us-west-2.amazonaws.com';

/**
 * Request a short-lived S3 upload URL from portal-api.
 * Backend contract (Action: getS3UploadUrl):
 *   request body: { fileName, contentType, fileSize }
 *   response JSON (any of):
 *     { uploadUrl, publicUrl, key }
 *     { data: { uploadUrl, publicUrl, key } }
 *     { url / presignedUrl, publicUrl / fileUrl, key / objectKey }
 */
const requestPresignedUpload = async (token, file) => {
  const safeName = String(file.name || 'upload.bin').replace(/[^\w.\-()+ ]+/g, '_');
  const objectKey = `${Date.now()}-${safeName}`;
  const payload = {
    fileName: safeName,
    objectKey,
    contentType: file.type || 'application/octet-stream',
    fileSize: file.size || 0,
    bucket: 'portal-upload-repository',
    region: 'us-west-2',
    acl: 'public-read',
  };

  const response = await postApiData(basePCUrl, token, payload, 'getS3UploadUrl');
  if (!response || response.error) {
    throw new Error(response?.message || 'Failed to request upload URL');
  }

  const body = response.data || response.result || response;
  const uploadUrl =
    body.uploadUrl || body.presignedUrl || body.url || body.putUrl || null;
  const key = body.key || body.objectKey || objectKey;
  const publicUrl =
    body.publicUrl ||
    body.fileUrl ||
    body.public_url ||
    `${PUBLIC_BUCKET_BASE}/${String(key).replace(/ /g, '+')}`;

  if (!uploadUrl) {
    throw new Error(
      'Upload service did not return a presigned URL (Action getS3UploadUrl).'
    );
  }

  return { uploadUrl, publicUrl, key };
};

const putFileWithProgress = (uploadUrl, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`S3 upload failed with HTTP ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('Network error during S3 upload'));
    xhr.send(file);
  });

const ImageUploadS3 = (props) => {
  const { getAccessTokenSilently } = usePortalAuth();
  const handleLinkChange = props.handleLinkChange;
  const progress = props.progress;
  const setProgress = props.setProgress;

  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileInput = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setErrorMessage('');
    setIsUploading(true);
    setProgress(0);

    try {
      const token = await getAccessTokenSilently();
      const { uploadUrl, publicUrl, key } = await requestPresignedUpload(token, file);
      await putFileWithProgress(uploadUrl, file, setProgress);

      // Keep legacy callback shape (file-like) plus publicUrl for callers that want it.
      handleLinkChange({
        name: key || file.name,
        publicUrl,
        size: file.size,
        type: file.type,
      });
    } catch (err) {
      console.error('Presigned upload failed:', err);
      setProgress(0);
      setErrorMessage(
        err?.message ||
          'Upload failed. Ensure portal-api implements getS3UploadUrl presign.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const useStyles = makeStyles({
    root: {
      width: '100%',
    },
  });

  const classes = useStyles();

  return (
    <div>
      <div>File Upload Progress is {progress || 0}%</div>
      <div className={classes.root}>
        <LinearProgress variant="determinate" value={Number(progress) || 0} />
      </div>
      <br />
      <input type="file" onChange={handleFileInput} disabled={isUploading} />
      {selectedFile ? (
        <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
          {selectedFile.name}
        </Typography>
      ) : null}
      {errorMessage ? (
        <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
          {errorMessage}
        </Typography>
      ) : null}
    </div>
  );
};

export default ImageUploadS3;
